'use client'

import type { DetailedHTMLProps } from 'react'

import cn from 'clsx'
import { Box } from '@/components/Box/Box'
import { Card, type CardProps } from '@/components/Card/Card'
import { type BaseImageProps, Image } from '@/components/image/image'
import { Text } from '@/components/Text/Text'

import css from './icon-card.module.css'

export type IconCardProps = {
    title: string
    description?: string | React.ReactNode
    descriptionTitle?: string
    image: BaseImageProps
    link: DetailedHTMLProps<React.AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>
    size?: 'base' | 'lg'
    badge?: React.ReactNode
    disabled?: boolean
    externalIcon?: React.ReactElement
}

export function IconCard({
    title,
    description,
    descriptionTitle,
    link,
    image,
    size = 'base',
    badge,
    className,
    externalIcon,
    ...rest
}: IconCardProps & Omit<CardProps, 'children' | 'size'>) {
    const imageSize = size === 'base' ? 40 : 96
    const isDescriptionComponent = typeof description !== 'string'
    return (
        <Card className={cn(css.iconCard, css[size], className)} {...rest}>
            <a className={cn(css.link, link.className)} {...link}>
                <Image width={imageSize} height={imageSize} {...image} />
                <Box className={css.content}>
                    <div className={css.title}>
                        <Text size="minor-base" title={title}>
                            {title}
                        </Text>
                        {link?.target === '_blank' ? (
                            <div className={css.externalIcon}>{externalIcon}</div>
                        ) : null}
                    </div>
                    {description ? (
                        <Text
                            size="body-xs"
                            className={cn(css.description, {
                                [css.descriptionComponent]: isDescriptionComponent,
                            })}
                            title={!isDescriptionComponent ? description : descriptionTitle}
                        >
                            {description}
                        </Text>
                    ) : null}
                </Box>
            </a>
            {badge ? <span className={css.badge}>{badge}</span> : null}
        </Card>
    )
}
